import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api, API, getToken, setToken } from "@/lib/api";
import { Download, LogOut, RefreshCw, Send, Mail, Award } from "lucide-react";

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");
  const [sendingAnnouncement, setSendingAnnouncement] = useState(false);
  const [sendingAccreditation, setSendingAccreditation] = useState(false);
  const [resendingId, setResendingId] = useState(null);

  async function load() {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/registrations");
      setItems(data.items || []);
    } catch (err) {
      if (err?.response?.status === 401) {
        setToken(null);
        navigate("/admin/login");
      } else {
        toast.error("Failed to load registrations");
      }
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    if (!getToken()) {
      navigate("/admin/login");
      return;
    }
    queueMicrotask(() => {
      load();
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function logout() {
    setToken(null);
    navigate("/admin/login");
  }

  async function downloadCsv() {
    const token = getToken();
    try {
      const res = await fetch(`${API}/admin/registrations/export`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error("Export failed");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `registrations_${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("CSV downloaded");
    } catch (e) {
      toast.error("Could not export CSV");
    }
  }

  async function sendVenueChange() {
    if (
      !window.confirm(
        "Send the venue-change announcement email to all registered delegates? Delegates who have already received it will be skipped automatically."
      )
    ) {
      return;
    }
    setSendingAnnouncement(true);
    try {
      const { data } = await api.post("/admin/announcements/venue-change");
      toast.success(
        `Venue update sent · ${data.sent} delivered · ${data.skipped} skipped`
      );
    } catch (e) {
      toast.error("Could not send announcement");
    } finally {
      setSendingAnnouncement(false);
    }
  }

  async function sendAccreditation() {
    if (
      !window.confirm(
        "Send the PMC accreditation announcement (4 CME points) to all registered delegates? Emails will be paced 1 per minute. Delegates who already received it will be skipped."
      )
    ) {
      return;
    }
    setSendingAccreditation(true);
    try {
      const { data } = await api.post("/admin/announcements/accreditation");
      toast.success(
        `Queued ${data.queued} email${data.queued === 1 ? "" : "s"} (1 per minute) · ${data.skipped} skipped`
      );
    } catch (e) {
      toast.error("Could not queue accreditation emails");
    } finally {
      setSendingAccreditation(false);
    }
  }

  async function resendConfirmation(reg) {
    if (
      !window.confirm(
        `Resend the confirmation email to ${reg.email}?`
      )
    ) {
      return;
    }
    setResendingId(reg.id);
    try {
      await api.post(`/admin/registrations/${reg.id}/resend`);
      toast.success(`Confirmation re-sent to ${reg.email}`);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Could not resend email");
    } finally {
      setResendingId(null);
    }
  }

  const filtered = items.filter((r) => {
    if (!query) return true;
    const q = query.toLowerCase();
    return (
      r.full_name?.toLowerCase().includes(q) ||
      r.email?.toLowerCase().includes(q) ||
      r.city?.toLowerCase().includes(q) ||
      r.mci_number?.toLowerCase().includes(q)
    );
  });

  return (
    <main
      data-testid="admin-dashboard"
      className="min-h-screen bg-[#F5F2EA] py-10 px-6 md:px-12"
    >
      <header className="flex flex-wrap items-end justify-between gap-4 border-b border-[#C5A059]/30 pb-6">
        <div>
          <p className="text-[11px] uppercase tracking-[0.28em] text-[#A4863F]">
            Aesthetic Symposium 2026
          </p>
          <h1 className="font-serif text-4xl md:text-5xl text-[#1A1A1A] mt-1">
            Registrations
          </h1>
          <p className="mt-2 text-sm text-[#4A4A4A]" data-testid="reg-count">
            {loading ? "Loading…" : `${items.length} delegate${items.length === 1 ? "" : "s"} registered`}
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <button
            onClick={load}
            data-testid="refresh-btn"
            className="inline-flex items-center gap-2 border border-[#C5A059]/50 px-4 py-2.5 text-xs uppercase tracking-[0.18em] hover:bg-[#EBE7DD] transition-colors"
          >
            <RefreshCw className="w-3.5 h-3.5" />
            Refresh
          </button>
          <button
            onClick={downloadCsv}
            data-testid="export-csv-btn"
            className="inline-flex items-center gap-2 bg-[#059669] text-white px-4 py-2.5 text-xs uppercase tracking-[0.18em] hover:bg-[#0F5C46] transition-colors"
          >
            <Download className="w-3.5 h-3.5" />
            Export CSV
          </button>
          <button
            onClick={sendVenueChange}
            disabled={sendingAnnouncement}
            data-testid="send-venue-change-btn"
            className="inline-flex items-center gap-2 border border-[#C5A059] text-[#1A1A1A] px-4 py-2.5 text-xs uppercase tracking-[0.18em] hover:bg-[#C5A059] hover:text-white transition-colors disabled:opacity-60"
          >
            <Send className="w-3.5 h-3.5" />
            {sendingAnnouncement ? "Sending…" : "Send venue update"}
          </button>
          <button
            onClick={sendAccreditation}
            disabled={sendingAccreditation}
            data-testid="send-accreditation-btn"
            className="inline-flex items-center gap-2 border border-[#0F5C46] text-[#0F5C46] px-4 py-2.5 text-xs uppercase tracking-[0.18em] hover:bg-[#0F5C46] hover:text-white transition-colors disabled:opacity-60"
          >
            <Award className="w-3.5 h-3.5" />
            {sendingAccreditation ? "Queueing…" : "Send PMC accreditation"}
          </button>
          <button
            onClick={logout}
            data-testid="logout-btn"
            className="inline-flex items-center gap-2 border border-[#1A1A1A]/20 px-4 py-2.5 text-xs uppercase tracking-[0.18em] hover:bg-[#1A1A1A] hover:text-white transition-colors"
          >
            <LogOut className="w-3.5 h-3.5" />
            Sign out
          </button>
        </div>
      </header>

      <div className="mt-6">
        <input
          type="search"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search by name, email, city or MCI no."
          data-testid="search-input"
          className="w-full md:w-96 bg-white border border-[#C5A059]/40 px-4 py-2.5 text-sm focus:outline-none focus:border-[#0F5C46]"
        />
      </div>

      <div className="mt-6 overflow-x-auto border border-[#C5A059]/30 bg-white">
        <table className="w-full text-sm" data-testid="registrations-table">
          <thead className="bg-[#EBE7DD]/60">
            <tr className="text-left text-[10px] uppercase tracking-[0.18em] text-[#4A4A4A]">
              <th className="px-4 py-3">Date</th>
              <th className="px-4 py-3">Name</th>
              <th className="px-4 py-3">Qualification</th>
              <th className="px-4 py-3">Specialty</th>
              <th className="px-4 py-3">MCI No.</th>
              <th className="px-4 py-3">Clinic / Hospital</th>
              <th className="px-4 py-3">City</th>
              <th className="px-4 py-3">Mobile</th>
              <th className="px-4 py-3">Email</th>
              <th className="px-4 py-3">Injecting</th>
              <th className="px-4 py-3">Interests</th>
              <th className="px-4 py-3">LinkedIn / Instagram</th>
              <th className="px-4 py-3 text-right">Action</th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 && !loading && (
              <tr>
                <td colSpan={13} className="px-4 py-12 text-center text-[#4A4A4A]">
                  No registrations yet.
                </td>
              </tr>
            )}
            {filtered.map((r) => (
              <tr
                key={r.id}
                data-testid={`registration-row-${r.id}`}
                className="border-t border-[#C5A059]/15 hover:bg-[#FBF8F1]"
              >
                <td className="px-4 py-3 whitespace-nowrap text-[#4A4A4A]">
                  {r.created_at ? new Date(r.created_at).toLocaleString() : ""}
                </td>
                <td className="px-4 py-3 font-medium">{r.full_name}</td>
                <td className="px-4 py-3">{r.qualification}</td>
                <td className="px-4 py-3">{r.specialty}</td>
                <td className="px-4 py-3">{r.mci_number}</td>
                <td className="px-4 py-3">{r.clinic_or_hospital}</td>
                <td className="px-4 py-3">{r.city}</td>
                <td className="px-4 py-3 whitespace-nowrap">{r.mobile}</td>
                <td className="px-4 py-3">{r.email}</td>
                <td className="px-4 py-3 capitalize">{r.currently_injecting}</td>
                <td className="px-4 py-3">
                  {(r.areas_of_interest || []).join(", ")}
                </td>
                <td className="px-4 py-3">
                  {r.social_profile ? (
                    <a
                      href={r.social_profile}
                      target="_blank"
                      rel="noreferrer"
                      className="text-[#0F5C46] underline hover:text-[#1A1A1A] break-all"
                    >
                      {r.social_profile.length > 40
                        ? r.social_profile.slice(0, 40) + "…"
                        : r.social_profile}
                    </a>
                  ) : (
                    <span className="text-[#A4863F]">—</span>
                  )}
                </td>
                <td className="px-4 py-3 text-right whitespace-nowrap">
                  <button
                    onClick={() => resendConfirmation(r)}
                    disabled={resendingId === r.id}
                    data-testid={`resend-btn-${r.id}`}
                    title={`Resend confirmation to ${r.email}`}
                    className="inline-flex items-center gap-1.5 border border-[#C5A059]/60 px-3 py-1.5 text-[10px] uppercase tracking-[0.18em] text-[#1A1A1A] hover:bg-[#C5A059] hover:text-white transition-colors disabled:opacity-60"
                  >
                    <Mail className="w-3 h-3" />
                    {resendingId === r.id ? "Sending…" : "Resend"}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </main>
  );
}
